/**
 *  @class
 *  @function Pagination
 */

// Take scroll restoration over from the browser: its own restore runs after
// ours and would override it.
if ('scrollRestoration' in history) { history.scrollRestoration = 'manual'; }

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector('[data-id=' + id + ']');
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.i = 2;
      this.isRestoring = false;
    }
    connectedCallback() {
      if (this.classList.contains('pagination-type--loadmore')) {
        this.loadMore();
      }
      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
      }
      this.restoreScrollPosition();
    }
    addUrlParam(search, key) {
      var newParam = key + '=' + this.i,
        params = '?' + newParam;

      if (search) {
        params = search.replace(new RegExp('([?&])' + key + '[^&]*'), '$1' + newParam);
        if (params === search) {
          params += '&' + newParam;
        }
      }

      return params;
    }
    loadMore() {
      let base = this;
      this.button.addEventListener('click', function () {
        base.loadProducts();
        this.blur();
        return false;
      });
    }
    infinite() {
      let base = this;
      base.observer = new IntersectionObserver(function (entries) {
        if (entries[0].intersectionRatio === 1) {
          base.loadProducts();
        }
      }, {
        threshold: [0, 1]
      });
      base.observer.observe(base);
    }

    /* ========================================
       SCROLL POSITION SAVE / RESTORE

       2026-08-22 (to-do 31): scrollRestoration is now manual, the restore
       retries until the lazy-loaded grid is tall enough, and beforeunload was
       replaced by pagehide so bfcache still works.
       ======================================== */

    saveScrollPosition() {
      var key = 'collectionScroll_' + window.location.pathname + window.location.search;
      var data = {
        scrollY: window.scrollY,
        pagesLoaded: this.i - 1,
        timestamp: Date.now()
      };
      try {
        sessionStorage.setItem(key, JSON.stringify(data));
      } catch (e) {}
    }

    scrollToTarget(target) {
      var attempts = 0;
      var settle = function () {
        window.scrollTo(0, target);
        attempts++;
        // keep trying while the grid is still growing (lazy images), max ~2s
        if (attempts < 60 && Math.abs(window.scrollY - target) > 2) {
          requestAnimationFrame(settle);
        }
      };
      requestAnimationFrame(settle);
      window.addEventListener('load', function () { window.scrollTo(0, target); }, { once: true });
    }

    restoreScrollPosition() {
      var key = 'collectionScroll_' + window.location.pathname + window.location.search;
      var saved;
      try {
        saved = JSON.parse(sessionStorage.getItem(key));
      } catch (e) {
        return;
      }
      if (!saved) return;

      // Only restore if saved less than 30 minutes ago
      if (Date.now() - saved.timestamp > 30 * 60 * 1000) {
        sessionStorage.removeItem(key);
        return;
      }

      // Check if this is a back/forward navigation
      var navEntries = performance.getEntriesByType('navigation');
      var isBackForward = navEntries.length > 0 && navEntries[0].type === 'back_forward';
      var isBackForwardLegacy = performance.navigation && performance.navigation.type === 2;

      if (!isBackForward && !isBackForwardLegacy) {
        return;
      }

      var pagesToLoad = saved.pagesLoaded;
      var scrollTarget = saved.scrollY;

      if (pagesToLoad <= 1) {
        this.scrollToTarget(scrollTarget);
        sessionStorage.removeItem(key);
        return;
      }

      // Disconnect infinite scroll observer during restore
      if (this.observer) {
        this.observer.unobserve(this);
      }

      this.isRestoring = true;
      var originalAnimations = this.animations_enabled;
      this.animations_enabled = false;

      var base = this;

      // Build all page URLs (pages 2 through pagesToLoad)
      var pageNumbers = [];
      for (var p = 2; p <= pagesToLoad; p++) {
        pageNumbers.push(p);
      }

      // Fetch ALL pages in parallel for maximum speed
      Promise.all(
        pageNumbers.map(function(pageNum) {
          var pageParam = '?page=' + pageNum;
          if (document.location.search) {
            pageParam = document.location.search.replace(/([?&])page=[^&]*/, '$1page=' + pageNum);
            if (pageParam === document.location.search) {
              pageParam += '&page=' + pageNum;
            }
          }
          var url = document.location.pathname + pageParam;

          return fetch(url)
            .then(function(r) { return r.text(); })
            .then(function(html) { return { page: pageNum, html: html }; })
            .catch(function() { return { page: pageNum, html: null }; });
        })
      ).then(function(results) {
        // Sort by page number to insert in correct order
        results.sort(function(a, b) { return a.page - b.page; });

        // Use a document fragment for faster DOM insertion
        var fragment = document.createDocumentFragment();

        results.forEach(function(result) {
          if (!result.html) return;
          var parsed = new DOMParser().parseFromString(result.html, 'text/html');
          var gridContent = parsed.getElementById('product-grid');
          if (!gridContent) return;

          var products = gridContent.querySelectorAll('.column');
          for (var j = 0; j < products.length; j++) {
            // Make visible immediately - skip animations
            var card = products[j].querySelector('.product-card .product-featured-image-link, .products .gallery--item figure');
            if (card) {
              card.style.opacity = '1';
              card.style.transform = 'scale(1)';
            }
            fragment.appendChild(products[j]);
          }
        });

        // Single DOM write — much faster than appending one by one
        base.grid.appendChild(fragment);

        // Update page counter so infinite scroll continues correctly
        base.i = pagesToLoad + 1;
        base.animations_enabled = originalAnimations;
        base.isRestoring = false;

        // Re-enable infinite scroll
        if (base.observer) {
          base.observer.observe(base);
        }

        // Scroll to saved position
        base.scrollToTarget(scrollTarget);

        sessionStorage.removeItem(key);
      });
    }

    /* ========================================
       END SCROLL POSITION SAVE / RESTORE
       ======================================== */

    loadProducts() {
      let base = this,
        url = document.location.pathname + base.addUrlParam(document.location.search, 'page');
      if (base.getAttribute('loading')) {
        return;
      }
      base.i++;
      base.setAttribute('loading', true);

      // Save scroll state whenever new products are loaded
      base.saveScrollPosition();

      fetch(url)
        .then(response => response.text())
        .then((responseText) => {
          const html = responseText;
          base.renderProducts(html, url);

          // Update saved state after render
          base.saveScrollPosition();

          dispatchCustomEvent('pagination:page-changed', {
            url: url
          });
        });
    }
    renderProducts(html) {
      let base = this,
        parsed_html = new DOMParser().parseFromString(html, 'text/html'),
        grid_to_replace = parsed_html.getElementById('product-grid'),
        count_to_replace = parsed_html.getElementById('ProductCount');

      if (!grid_to_replace) {
        if (base.observer) {
          base.observer.unobserve(base);
        }
        base.removeAttribute('loading');
        if (base.button) {
          base.button.classList.add('visually-hidden');
        }
        return;
      }
      let products = grid_to_replace.querySelectorAll('.column');

      for (var i = 0; i < products.length; i++) {
        products[i].dataset.pagination = base.i;
        this.grid.appendChild(products[i]);
      }
      if (this.animations_enabled) {
        let added_columns = this.grid.querySelectorAll('[data-pagination="' + base.i + '"]'),
          to_animate = [];

        for (var j = 0; j < added_columns.length; j++) {
          to_animate[j] = added_columns[j].querySelector('.product-card .product-featured-image-link, .products .gallery--item figure');
        }
        gsap.to(to_animate, { scale: 1, opacity: 1, stagger: 0.15, ease: window.theme.settings.animation_easing });
      }
      base.removeAttribute('loading');

      // pagination.
      if (count_to_replace) {
        const container = document.getElementsByClassName('thb-filter-count');

        for (var item of container) {
          item.innerHTML = count_to_replace.innerHTML;
        }
      }
    }
  }
  customElements.define('pagination-theme', Pagination);
}

/* ========================================
   GLOBAL: Save scroll position on link clicks
   ======================================== */
(function() {
  document.addEventListener('click', function(e) {
    // Cheap bail-out first: nothing to save on pages without a pagination element.
    var pagination = document.querySelector('pagination-theme');
    if (!pagination || typeof pagination.saveScrollPosition !== 'function') return;

    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;

    // Product links (the original behaviour) plus any same-origin link that
    // navigates away from the collection.
    var link = target.closest('a[href]');
    if (!link || !link.href) return;

    var isProductLink = link.href.indexOf('/products/') !== -1;
    var isSameOrigin = link.origin === window.location.origin;
    if (!isProductLink && !isSameOrigin) return;

    pagination.saveScrollPosition();
  });

  window.addEventListener('pagehide', function () {
    var pagination = document.querySelector('pagination-theme');
    if (pagination && typeof pagination.saveScrollPosition === 'function') {
      pagination.saveScrollPosition();
    }
  });

  // If the browser restored the page from bfcache it has already put the
  // customer back where they were - drop our saved state so nothing fights it.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      try {
        sessionStorage.removeItem('collectionScroll_' + window.location.pathname + window.location.search);
      } catch (err) {}
    }
  });
})();
